import { supabase } from './supabase';

/** Steam personastate: 0=offline, 1=online, 2=busy, 3=away, 4=snooze, 5=looking to trade, 6=looking to play */
export function isSteamOnline(personaState, isPlayingNow = false) {
  if (isPlayingNow) return true;
  return personaState === 1 || personaState === 6;
}

export function formatGameActivityLabel(game) {
  if (game?.is_currently_playing) return null;

  const lastPlayed = formatLastPlayed(game?.last_played_at);
  if (lastPlayed) return `Last played ${lastPlayed}`;

  if (game?.playtime_2weeks > 0) return 'Played in last 2 weeks';

  return null;
}

export function formatLastPlayed(isoDate) {
  if (!isoDate) return null;

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date(now).getFullYear() ? 'numeric' : undefined,
  });
}

function sortGames(games) {
  return [...games].sort((a, b) => {
    if (a.is_currently_playing !== b.is_currently_playing) {
      return a.is_currently_playing ? -1 : 1;
    }
    const aTime = new Date(a.last_played_at || a.updated_at).getTime();
    const bTime = new Date(b.last_played_at || b.updated_at).getTime();
    return bTime - aTime;
  });
}

function pickPrimaryGame(games) {
  const sorted = sortGames(games);
  return sorted[0] ?? null;
}

function mapActivityRow(row) {
  return {
    app_id: row.app_id,
    game_name: row.game_name,
    playtime_2weeks: row.playtime_2weeks,
    is_currently_playing: row.is_currently_playing,
    last_played_at: row.last_played_at,
    updated_at: row.updated_at,
  };
}

function buildFriendEntry(profile, activityRows = []) {
  const games = sortGames(activityRows.map(mapActivityRow));
  const primaryGame = pickPrimaryGame(games);

  return {
    friendId: profile.id,
    profile,
    games,
    primaryGame,
    isPlayingNow: games.some((game) => game.is_currently_playing),
  };
}

function sortFriends(friends) {
  return [...friends].sort((a, b) => {
    if (a.isPlayingNow !== b.isPlayingNow) return a.isPlayingNow ? -1 : 1;

    const aHasGames = a.games.length > 0;
    const bHasGames = b.games.length > 0;
    if (aHasGames !== bHasGames) return aHasGames ? -1 : 1;

    const aTime = a.primaryGame?.last_played_at
      ? new Date(a.primaryGame.last_played_at).getTime()
      : 0;
    const bTime = b.primaryGame?.last_played_at
      ? new Date(b.primaryGame.last_played_at).getTime()
      : 0;
    if (aTime !== bTime) return bTime - aTime;

    return (a.profile?.display_name ?? '').localeCompare(b.profile?.display_name ?? '');
  });
}

export function mergeFriendsWithActivities(profiles, activities) {
  const activitiesByUser = new Map();

  for (const row of activities) {
    if (!row.user_id) continue;
    if (!activitiesByUser.has(row.user_id)) {
      activitiesByUser.set(row.user_id, []);
    }
    activitiesByUser.get(row.user_id).push(row);
  }

  return sortFriends(
    profiles.map((profile) => buildFriendEntry(profile, activitiesByUser.get(profile.id) ?? [])),
  );
}

export function groupActivitiesByFriend(activities) {
  const byFriend = new Map();

  for (const row of activities) {
    const friendId = row.user_id;
    if (!friendId) continue;

    if (!byFriend.has(friendId)) {
      byFriend.set(friendId, {
        friendId,
        profile: row.profiles,
        games: [],
      });
    }

    byFriend.get(friendId).games.push(mapActivityRow(row));
  }

  return sortFriends(
    Array.from(byFriend.values()).map((friend) => {
      const games = sortGames(friend.games);
      return {
        friendId: friend.friendId,
        profile: friend.profile,
        games,
        primaryGame: pickPrimaryGame(games),
        isPlayingNow: games.some((game) => game.is_currently_playing),
      };
    }),
  );
}

const PROFILE_SELECT = 'id, display_name, avatar_url, steam_persona_state';

const ACTIVITY_SELECT = `
  user_id,
  app_id,
  game_name,
  playtime_2weeks,
  is_currently_playing,
  last_played_at,
  updated_at
`;

export async function fetchFriendActivities(userId) {
  const [friendsResult, activityResult] = await Promise.all([
    supabase
      .from('profiles')
      .select(PROFILE_SELECT)
      .neq('id', userId),
    supabase
      .from('user_activity')
      .select(ACTIVITY_SELECT)
      .neq('user_id', userId)
      .order('is_currently_playing', { ascending: false })
      .order('updated_at', { ascending: false }),
  ]);

  if (friendsResult.error) throw friendsResult.error;
  if (activityResult.error) throw activityResult.error;

  return mergeFriendsWithActivities(friendsResult.data ?? [], activityResult.data ?? []);
}
